import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, FileText, Sparkles, UserRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DataKeys } from "../../types";
import { getHealthModulePanelMotion } from "../tools/Animations";
import { getEducationModulePreviewSrc } from "./educationModulePreviewAssets";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../health/healthTypes";
import {
    healthClinicalAgentsActivePanel,
    healthClinicalAgentsDetailSummary,
    healthClinicalAgentsDetailTitle,
    healthClinicalAgentsIoBlock,
    healthClinicalAgentsIoBlockLabel,
    healthClinicalAgentsIoBlockLabelRow,
    healthClinicalAgentsIoCheck,
    healthClinicalAgentsIoGrid,
    healthClinicalAgentsIoItem,
    healthClinicalAgentsIoItemText,
    healthClinicalAgentsIoList,
    healthClinicalAgentsIoSection,
    healthClinicalAgentsStageImage,
    healthClinicalAgentsStageImageWrap,
    healthClinicalAgentsStageIntro,
    healthClinicalAgentsSummaryLabel,
    healthClinicalAgentsSummaryPanel,
    healthClinicalAgentsWorkflowLabel,
    healthClinicalAgentsWorkflowNote,
    healthClinicalAgentsWorkflowText,
} from "../health/healthVisualStyles";
import { HealthProductStatusBadge } from "../health/HealthProductStatusBadge";

const METADATA_IO_KEYS = ["input", "output"] as const;

const IO_BLOCK_ICONS: Record<(typeof METADATA_IO_KEYS)[number], LucideIcon> = {
    input: UserRound,
    output: FileText,
};

const educationModuleStageShell =
    "flex w-full min-w-0 flex-col gap-6 text-left [text-align:left] lg:gap-8";

const educationModuleMediaGrid =
    "grid w-full min-w-0 grid-cols-1 gap-6 text-left [text-align:left] lg:grid-cols-2 lg:items-stretch lg:gap-8 xl:gap-10";

const educationModuleDetailsCol =
    "flex min-h-0 flex-col border-t border-grey-100/30 pt-6 text-left [text-align:left] lg:border-l lg:border-t-0 lg:pt-0 lg:pl-8 xl:pl-10";

function metadataToItems(value: string): string[] {
    return value
        .split(/[,，、;；]/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function ModuleIoBlock({
    label,
    items,
    icon: Icon,
}: {
    label: string;
    items: string[];
    icon: LucideIcon;
}) {
    return (
        <div className={healthClinicalAgentsIoBlock}>
            <div className={healthClinicalAgentsIoBlockLabelRow}>
                <Icon
                    className="h-4 w-4 shrink-0 text-teal-600"
                    strokeWidth={2}
                    aria-hidden
                />
                <p className={healthClinicalAgentsIoBlockLabel}>{label}</p>
            </div>
            <ul className={healthClinicalAgentsIoList}>
                {items.map((item, index) => (
                    <li key={index} className={healthClinicalAgentsIoItem}>
                        <span
                            className={healthClinicalAgentsIoCheck}
                            aria-hidden="true"
                        >
                            <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
                        </span>
                        <span className={healthClinicalAgentsIoItemText}>
                            {item}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

interface EducationModuleDetailPanelProps {
    item: DataKeys;
    metadataLabels: DataKeys | undefined;
    flowSummaryLabel: string;
    lang: HealthLang;
    transitionDirection?: number;
    panelId: string;
    tabId: string;
    previewAssetKey?: string;
}

export function EducationModuleDetailPanel({
    item,
    metadataLabels,
    flowSummaryLabel,
    lang,
    transitionDirection = 1,
    panelId,
    tabId,
    previewAssetKey,
}: EducationModuleDetailPanelProps) {
    const moduleId = item.id as string;
    const reduceMotion = useReducedMotion();
    const panelMotion = getHealthModulePanelMotion(
        transitionDirection,
        reduceMotion
    );
    const previewSrc = previewAssetKey
        ? getEducationModulePreviewSrc(previewAssetKey)
        : undefined;
    const isAvailable = moduleId === "simulation";
    const metadata = item.metadata as DataKeys | undefined;
    const valueLabel = metadataLabels?.value as LocalizedString | undefined;
    const valueText = metadata?.value as LocalizedString | undefined;
    const previewAlt = getLocalized(item.title as LocalizedString, lang);

    const ioBlocks = METADATA_IO_KEYS.flatMap((key) => {
        const label = metadataLabels?.[key] as LocalizedString | undefined;
        const value = metadata?.[key] as LocalizedString | undefined;
        if (!label || !value) return [];
        return [
            {
                key,
                label: getLocalized(label, lang),
                items: metadataToItems(getLocalized(value, lang)),
                icon: IO_BLOCK_ICONS[key],
            },
        ];
    });

    return (
        <motion.article
            layout
            transition={{ layout: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } }}
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId}
            className={`${healthClinicalAgentsActivePanel} scroll-mt-24 overflow-hidden`}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={moduleId}
                    initial={panelMotion.initial}
                    animate={panelMotion.animate}
                    exit={panelMotion.exit}
                    transition={panelMotion.transition}
                    className={educationModuleStageShell}
                >
                    <header className={healthClinicalAgentsStageIntro}>
                        <div className="flex flex-wrap items-center gap-2">
                            {isAvailable ? (
                                <HealthProductStatusBadge
                                    status="available"
                                    lang={lang}
                                />
                            ) : item.badge ? (
                                <span className="rounded-full border border-grey-200 bg-grey-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-grey-600">
                                    {getLocalized(
                                        item.badge as LocalizedString,
                                        lang
                                    )}
                                </span>
                            ) : null}
                        </div>
                        <h3 className={healthClinicalAgentsDetailTitle}>
                            {getLocalized(item.title as LocalizedString, lang)}
                        </h3>
                        <p className={healthClinicalAgentsDetailSummary}>
                            {getLocalized(item.text as LocalizedString, lang)}
                        </p>
                    </header>

                    <div className={educationModuleMediaGrid}>
                        {previewSrc ? (
                            <div
                                className={`${healthClinicalAgentsStageImageWrap} h-full min-h-[16rem] sm:min-h-[18rem] lg:min-h-[22rem]`}
                            >
                                <img
                                    src={previewSrc}
                                    alt={previewAlt}
                                    className={`${healthClinicalAgentsStageImage} lg:h-full lg:max-h-none`}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        ) : null}

                        {ioBlocks.length > 0 ? (
                            <div className={educationModuleDetailsCol}>
                                <div
                                    className={`${healthClinicalAgentsSummaryPanel} flex h-full min-h-0 flex-col`}
                                >
                                    <div className="flex min-h-0 flex-1 flex-col gap-5 lg:gap-6">
                                        <p
                                            className={
                                                healthClinicalAgentsSummaryLabel
                                            }
                                        >
                                            {flowSummaryLabel}
                                        </p>

                                        <div
                                            className={
                                                healthClinicalAgentsIoSection
                                            }
                                        >
                                            <div
                                                className={
                                                    healthClinicalAgentsIoGrid
                                                }
                                            >
                                                {ioBlocks.map((block) => (
                                                    <ModuleIoBlock
                                                        key={block.key}
                                                        label={block.label}
                                                        items={block.items}
                                                        icon={block.icon}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {valueLabel && valueText ? (
                                        <div
                                            className={`${healthClinicalAgentsWorkflowNote} mt-5 lg:mt-auto lg:pt-2`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <Sparkles
                                                    className="mt-0.5 h-5 w-5 shrink-0 text-teal-600"
                                                    strokeWidth={2}
                                                    aria-hidden
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <p
                                                        className={
                                                            healthClinicalAgentsWorkflowLabel
                                                        }
                                                    >
                                                        {getLocalized(
                                                            valueLabel,
                                                            lang
                                                        )}
                                                    </p>
                                                    <p
                                                        className={
                                                            healthClinicalAgentsWorkflowText
                                                        }
                                                    >
                                                        {getLocalized(
                                                            valueText,
                                                            lang
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </motion.div>
            </AnimatePresence>
        </motion.article>
    );
}
